import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { SECURITY_DEFAULT_DATE_RANGE, SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import type { WazuhDateRangeOption } from 'src/mindtime/hooks/useWazuhScope';
import {
  ensurePageFilters,
  resetPageFilters,
  setPageFilter,
  setPageSearch,
} from 'src/store/slices/securityFiltersSlice';

const EMPTY_FILTERS: Record<string, string> = {};

type UseSecurityPageFiltersOptions = {
  /** Applied only on first visit (no stored page state). */
  defaults?: Record<string, string>;
  searchDefault?: string;
  /** Search debounce ms; pass `false` to persist search immediately. */
  debounceMs?: number | false;
};

export const useSecurityPageFilters = (
  pageKey: string,
  {
    defaults = EMPTY_FILTERS,
    searchDefault = '',
    debounceMs = 300,
  }: UseSecurityPageFiltersOptions = {}
) => {
  const dispatch = useAppDispatch();
  const pageState = useAppSelector((state) => state.securityFilters.pages[pageKey]);
  const immediateSearch = debounceMs === false;

  useEffect(() => {
    if (!pageState) {
      dispatch(ensurePageFilters({ pageKey, filters: defaults, search: searchDefault }));
    }
  }, [dispatch, pageKey, pageState, defaults, searchDefault]);

  const filters = pageState?.filters ?? defaults;
  const search = pageState?.search ?? searchDefault;

  const setFilter = useCallback(
    (key: string, value: string) => {
      dispatch(setPageFilter({ pageKey, key, value }));
    },
    [dispatch, pageKey]
  );

  const persistSearch = useCallback(
    (value: string) => {
      dispatch(setPageSearch({ pageKey, search: value }));
    },
    [dispatch, pageKey]
  );

  const debouncedPersistSearch = useMemo(
    () => (immediateSearch ? null : debounce(persistSearch, debounceMs as number)),
    [immediateSearch, debounceMs, persistSearch]
  );

  useEffect(
    () => () => {
      debouncedPersistSearch?.cancel();
    },
    [debouncedPersistSearch]
  );

  const [searchInput, setSearchInputState] = useState(searchDefault);

  useEffect(() => {
    if (immediateSearch) return;
    setSearchInputState(pageState?.search ?? searchDefault);
  }, [immediateSearch, pageKey, pageState?.search, searchDefault]);

  const setSearchInput = useCallback(
    (value: string) => {
      if (immediateSearch) {
        persistSearch(value);
        return;
      }
      setSearchInputState(value);
      debouncedPersistSearch?.(value);
    },
    [debouncedPersistSearch, immediateSearch, persistSearch]
  );

  const resetFilters = useCallback(() => {
    debouncedPersistSearch?.cancel();
    dispatch(resetPageFilters({ pageKey, search: searchDefault }));
    if (!immediateSearch) {
      setSearchInputState(searchDefault);
    }
  }, [debouncedPersistSearch, dispatch, immediateSearch, pageKey, searchDefault]);

  return {
    filters,
    setFilter,
    search,
    searchInput: immediateSearch ? search : searchInput,
    setSearchInput,
    resetFilters,
  };
};

/** Shared Reset handler for EDR toolbars that include global date range. */
export const useSecurityEdrToolbarReset = (
  resetFilters: () => void,
  setDateRange: (range: WazuhDateRangeOption) => void,
  setPage: (page: number) => void
) =>
  useCallback(() => {
    resetFilters();
    setDateRange(SECURITY_DEFAULT_DATE_RANGE);
    setPage(0);
  }, [resetFilters, setDateRange, setPage]);

/** SOC dashboard search (no debounce — persisted immediately). */
export const useSocDashboardPageFilters = () =>
  useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.socDashboard, { debounceMs: false });
